/**
 * Services Section Swiper Initialization
 * Original implementation from script.js
 */

declare global {
    interface Window {
        Swiper: any;
        servicesSwiper?: any;
        jQuery: any;
    }
}

/**
 * Helper function to render pagination bullets for mobile
 */
function renderMobilePaginationBullets(swiper: any): void {
    if (!swiper || !swiper.pagination || window.innerWidth > 991) {
        return;
    }
    
    const totalSlides = swiper.slides.length;
    const paginationEl = document.getElementById('swiper-dot-style1');
    
    if (!paginationEl || totalSlides === 0) {
        return;
    }
    
    // Clear existing bullets
    paginationEl.innerHTML = '';
    
    // Create bullets for each slide
    for (let i = 0; i < totalSlides; i++) {
        const bullet = document.createElement('span');
        bullet.className = i === swiper.activeIndex 
            ? 'swiper-pagination-bullet swiper-pagination-bullet-active' 
            : 'swiper-pagination-bullet';
        bullet.setAttribute('data-index', i.toString());
        
        // Add click handler to navigate to the slide
        bullet.addEventListener('click', function() {
            if (swiper) {
                swiper.slideTo(i);
            }
        });
        
        paginationEl.appendChild(bullet);
    }
    
    // Update bullets array reference
    swiper.pagination.bullets = Array.from(paginationEl.querySelectorAll('.swiper-pagination-bullet'));
}

/**
 * Initialize Services Swiper carousel - Original implementation
 */
export function initializeServicesSection(containerElement: HTMLElement | null): void {
    if (!containerElement) {
        return;
    }

    // Wait for Swiper to be available
    const initSwiper = (): void => {
        if (typeof window.Swiper === 'undefined') {
            setTimeout(initSwiper, 100);
            return;
        }

        // Give a small delay to ensure DOM is fully rendered
        setTimeout(() => {
            try {
                const carouselElement = containerElement.querySelector('.services-one__carousel') as HTMLElement;
                
                if (!carouselElement) {
                    return;
                }

                // Create pagination element if it doesn't exist
                let paginationEl = document.getElementById('swiper-dot-style1');
                if (!paginationEl) {
                    paginationEl = document.createElement('div');
                    paginationEl.id = 'swiper-dot-style1';
                    paginationEl.className = 'swiper-pagination';
                    carouselElement.appendChild(paginationEl);
                }

                // Original Swiper configuration from script.js
                const servicesSwiper = new window.Swiper('.services-one__carousel', {
                    "slidesPerView": 1,
                    "spaceBetween": 0,
                    "speed": 600,
                    "loop": false, // Changed to false for mobile edge visibility
                    "observer": true,
                    "observeParents": true,
                    "watchSlidesProgress": true,
                    "watchSlidesVisibility": true,
                    "preloadImages": true,
                    "updateOnWindowResize": true,
                    // Enable mouse drag/swipe on desktop (simulateTouch enables mouse dragging)
                    "simulateTouch": true,
                    "allowTouchMove": true,
                    "touchRatio": 1,
                    "allowSlideNext": true,
                    "allowSlidePrev": true,
                    // Disable mouse wheel scrolling
                    "mousewheel": false,
                    "keyboard": false,
                    "pagination": {
                        "el": "#swiper-dot-style1",
                        "type": "bullets",
                        "clickable": true,
                        "dynamicBullets": false,
                        "renderBullet": function (index: number, className: string) {
                            return '<span class="' + className + '" data-index="' + index + '"></span>';
                        }
                    },
                    "on": {
                        "init": function(this: any) {
                            // Initial pagination setup
                            const bullets = Array.from(this.pagination?.bullets || []) as HTMLElement[];
                            if (bullets.length) {
                                const activeIndex = this.activeIndex;
                                bullets.forEach((bullet: HTMLElement, index: number) => {
                                    bullet.classList.remove('swiper-pagination-bullet-active');
                                    if (index === activeIndex) {
                                        bullet.classList.add('swiper-pagination-bullet-active');
                                    }
                                });
                            }
                        },
                        "slideChange": function(this: any) {
                            const bullets = Array.from(this.pagination?.bullets || []) as HTMLElement[];
                            if (bullets.length) {
                                const activeIndex = this.activeIndex;
                                bullets.forEach((bullet: HTMLElement, index: number) => {
                                    bullet.classList.remove('swiper-pagination-bullet-active');
                                    if (index === activeIndex) {
                                        bullet.classList.add('swiper-pagination-bullet-active');
                                    }
                                });
                            }
                        },
                        "slideChangeTransitionStart": function(this: any) {
                            const bullets = Array.from(this.pagination?.bullets || []) as HTMLElement[];
                            if (bullets.length) {
                                const activeIndex = this.activeIndex;
                                bullets.forEach((bullet: HTMLElement, index: number) => {
                                    bullet.classList.remove('swiper-pagination-bullet-active');
                                    if (index === activeIndex) {
                                        bullet.classList.add('swiper-pagination-bullet-active');
                                    }
                                });
                            }
                        },
                        "slideChangeTransitionEnd": function(this: any) {
                            const bullets = Array.from(this.pagination?.bullets || []) as HTMLElement[];
                            if (bullets.length) {
                                const activeIndex = this.activeIndex;
                                bullets.forEach((bullet: HTMLElement, index: number) => {
                                    bullet.classList.remove('swiper-pagination-bullet-active');
                                    if (index === activeIndex) {
                                        bullet.classList.add('swiper-pagination-bullet-active');
                                    }
                                });
                            }
                        },
                        "touchEnd": function(this: any) {
                            const bullets = Array.from(this.pagination?.bullets || []) as HTMLElement[];
                            if (bullets.length) {
                                const activeIndex = this.activeIndex;
                                bullets.forEach((bullet: HTMLElement, index: number) => {
                                    bullet.classList.remove('swiper-pagination-bullet-active');
                                    if (index === activeIndex) {
                                        bullet.classList.add('swiper-pagination-bullet-active');
                                    }
                                });
                            }
                        },
                        "snapIndexChange": function(this: any) {
                            const bullets = Array.from(this.pagination?.bullets || []) as HTMLElement[];
                            if (bullets.length) {
                                const snapIndex = this.snapIndex;
                                bullets.forEach((bullet: HTMLElement, index: number) => {
                                    bullet.classList.remove('swiper-pagination-bullet-active');
                                    if (index === snapIndex) {
                                        bullet.classList.add('swiper-pagination-bullet-active');
                                    }
                                });
                            }
                        }
                    },
                    "navigation": {
                        "nextEl": "#swiper-button-prev1",
                        "prevEl": "#swiper-button-next1"
                    },
                    "autoplay": window.innerWidth > 991 ? {
                        "delay": 6000,
                        "disableOnInteraction": false
                    } : false,
                    "breakpoints": {
                        "0": {
                            "spaceBetween": 15,
                            "slidesPerView": 1.15,
                            "centeredSlides": false,
                            "loop": false
                        },
                        "375": {
                            "spaceBetween": 15,
                            "slidesPerView": 1.15,
                            "centeredSlides": false,
                            "loop": false
                        },
                        "575": {
                            "spaceBetween": 15,
                            "slidesPerView": 1.15,
                            "centeredSlides": false,
                            "loop": false
                        },
                        "768": {
                            "spaceBetween": 30,
                            "slidesPerView": 2,
                            "centeredSlides": false,
                            "loop": false
                        },
                        "992": {
                            "spaceBetween": 30,
                            "slidesPerView": 3,
                            "centeredSlides": false,
                            "loop": true
                        },
                        "1200": {
                            "spaceBetween": 30,
                            "slidesPerView": 3,
                            "centeredSlides": false,
                            "loop": true
                        },
                        "1320": {
                            "spaceBetween": 30,
                            "slidesPerView": 3,
                            "centeredSlides": false,
                            "loop": true
                        }
                    },
                });

                // Store swiper instance globally for updates
                window.servicesSwiper = servicesSwiper;
                
                // Fix: Only allow swiping when mouse button is held down (desktop mode)
                // Similar to old code - just stop tracking when mouse is released
                if (window.innerWidth > 991) {
                    // Stop tracking immediately on mouseup
                    const handleMouseUp = (e: MouseEvent) => {
                        if (e.button === 0 && window.innerWidth > 991) {
                            if (servicesSwiper && servicesSwiper.touchEventsData) {
                                servicesSwiper.touchEventsData.isTouched = false;
                                servicesSwiper.touchEventsData.isMoved = false;
                            }
                        }
                    };
                    
                    // Also check on mousemove if button is released (safety check)
                    const handleMouseMove = (e: MouseEvent) => {
                        // Only check on desktop and if Swiper is currently tracking
                        if (window.innerWidth > 991 && servicesSwiper && servicesSwiper.touchEventsData && servicesSwiper.touchEventsData.isTouched) {
                            // Check if left mouse button is actually pressed (buttons: 1 = left button)
                            if ((e.buttons & 1) === 0) {
                                // Mouse button is not down - stop tracking
                                servicesSwiper.touchEventsData.isTouched = false;
                                servicesSwiper.touchEventsData.isMoved = false;
                            }
                        }
                    };
                    
                    // Listen for mouseup and mousemove to stop tracking
                    carouselElement.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                    
                    // Cleanup function
                    const originalDestroy = servicesSwiper.destroy;
                    servicesSwiper.destroy = function(deleteInstance?: boolean, cleanupStyles?: boolean) {
                        carouselElement.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                        return originalDestroy.call(this, deleteInstance, cleanupStyles);
                    };
                }
                
                // Initial setup based on screen size
                if (window.innerWidth <= 991) {
                    // Mobile - disable autoplay, enable touch, show pagination
                    if (servicesSwiper.autoplay) {
                        servicesSwiper.autoplay.stop();
                    }
                    servicesSwiper.allowTouchMove = true;
                    servicesSwiper.simulateTouch = true;
                    servicesSwiper.touchRatio = 1;
                    
                    // Show pagination on mobile
                    const paginationEl = document.getElementById('swiper-dot-style1');
                    if (paginationEl) {
                        paginationEl.style.display = 'flex';
                    }
                    
                    // Force pagination to render all bullets (one per slide) on mobile
                    renderMobilePaginationBullets(servicesSwiper);
                } else {
                    // Desktop - enable autoplay, enable mouse drag, hide pagination
                    if (servicesSwiper.autoplay) {
                        servicesSwiper.autoplay.start();
                    }
                    servicesSwiper.allowTouchMove = true;
                    servicesSwiper.simulateTouch = true;
                    servicesSwiper.touchRatio = 1;
                    servicesSwiper.allowSlideNext = true;
                    servicesSwiper.allowSlidePrev = true;
                    
                    // Hide pagination on desktop
                    const paginationEl = document.getElementById('swiper-dot-style1');
                    if (paginationEl) {
                        paginationEl.style.display = 'none';
                    }
                }
                
                // Handle window resize and breakpoint changes
                let resizeTimeout: number;
                const handleAutoplayAndPagination = () => {
                    if (window.servicesSwiper) {
                        if (window.innerWidth <= 991) {
                            // Mobile - disable autoplay, enable touch, show pagination
                            if (window.servicesSwiper.autoplay) {
                                window.servicesSwiper.autoplay.stop();
                            }
                            window.servicesSwiper.allowTouchMove = true;
                            window.servicesSwiper.simulateTouch = true;
                            window.servicesSwiper.touchRatio = 1;
                            
                            // Show pagination on mobile
                            const paginationEl = document.getElementById('swiper-dot-style1');
                            if (paginationEl) {
                                paginationEl.style.display = 'flex';
                            }
                            
                            // Re-render pagination bullets for mobile
                            renderMobilePaginationBullets(window.servicesSwiper);
                        } else {
                            // Desktop - enable autoplay, enable mouse drag, hide pagination
                            if (window.servicesSwiper.autoplay) {
                                window.servicesSwiper.autoplay.start();
                            }
                            window.servicesSwiper.allowTouchMove = true;
                            window.servicesSwiper.simulateTouch = true;
                            window.servicesSwiper.touchRatio = 1;
                            window.servicesSwiper.allowSlideNext = true;
                            window.servicesSwiper.allowSlidePrev = true;
                            
                            // Hide pagination on desktop
                            const paginationEl = document.getElementById('swiper-dot-style1');
                            if (paginationEl) {
                                paginationEl.style.display = 'none';
                            }
                        }
                    }
                };
                
                window.addEventListener('resize', function() {
                    clearTimeout(resizeTimeout);
                    resizeTimeout = window.setTimeout(handleAutoplayAndPagination, 250);
                });
                
                // Also listen for Swiper breakpoint changes
                servicesSwiper.on('breakpoint', handleAutoplayAndPagination);
                
                // Force pagination to show all bullets (not dynamic)
                if (servicesSwiper.pagination && servicesSwiper.pagination.render) {
                    servicesSwiper.pagination.render();
                    servicesSwiper.pagination.update();
                }

                // Adjust padding for first slide (no left space) - mobile only
                if (window.innerWidth <= 991) {
                    const container = document.querySelector('.services-one__carousel') as HTMLElement;
                    if (container) {
                        container.style.paddingLeft = '0';
                        container.classList.add('swiper-container-first');
                    }

                    // Listen for slide changes to adjust padding
                    servicesSwiper.on('slideChange', function() {
                        if (window.innerWidth <= 991) {
                            if (servicesSwiper.activeIndex === 0) {
                                // First slide - no left padding
                                container.style.paddingLeft = '0';
                                container.classList.add('swiper-container-first');
                            } else {
                                // Other slides - add padding to show previous slide edge
                                container.style.paddingLeft = '15px';
                                container.classList.remove('swiper-container-first');
                            }
                        }
                    });
                }

                // Update swiper after a short delay to ensure all content is loaded
                setTimeout(function() {
                    if (servicesSwiper) {
                        servicesSwiper.update();
                        servicesSwiper.updateSlides();
                        
                        // Re-render pagination bullets for mobile after update
                        renderMobilePaginationBullets(servicesSwiper);
                    }
                }, 500);

                // Ensure services Swiper updates after everything is loaded
                window.addEventListener('load', function() {
                    if (window.servicesSwiper) {
                        setTimeout(function() {
                            window.servicesSwiper.update();
                            window.servicesSwiper.updateSlides();
                            
                            // Re-render pagination bullets for mobile after load
                            renderMobilePaginationBullets(window.servicesSwiper);
                        }, 500);
                    }
                });

                // Update Swiper when images load
                const servicesImages = document.querySelectorAll('.services-one__img img');
                let loadedImages = 0;
                const totalImages = servicesImages.length;

                if (totalImages > 0) {
                    servicesImages.forEach(function(img: Element) {
                        const imageElement = img as HTMLImageElement;
                        if (imageElement.complete) {
                            loadedImages++;
                        } else {
                            imageElement.addEventListener('load', function() {
                                loadedImages++;
                                if (loadedImages === totalImages && window.servicesSwiper) {
                                    window.servicesSwiper.update();
                                    window.servicesSwiper.updateSlides();
                                    
                                    // Re-render pagination bullets for mobile after images load
                                    renderMobilePaginationBullets(window.servicesSwiper);
                                }
                            });
                        }
                    });

                    if (loadedImages === totalImages && window.servicesSwiper) {
                        window.servicesSwiper.update();
                        window.servicesSwiper.updateSlides();
                        
                        // Re-render pagination bullets for mobile if all images already loaded
                        renderMobilePaginationBullets(window.servicesSwiper);
                    }
                }

            } catch (error) {
            }
        }, 100);
    };

    // Start initialization
    initSwiper();
}
